import React, { useContext } from "react";
import Color from "color";
import {
  ThemeProvider as StyledThemeProvider,
  createGlobalStyle,
} from "styled-components";
import {
  StylesProvider,
  ThemeProvider as MaterialThemeProvider,
  createTheme,
  responsiveFontSizes,
  CssBaseline,
} from "@material-ui/core";
//import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from "@date-io/moment";

import { UserContext } from "./UserContext";
import { FormatBold } from "@material-ui/icons";

const GlobalStyle = createGlobalStyle`
  *::-webkit-scrollbar {
    width: 8px;
  }
  *::-webkit-scrollbar-thumb {
    border-radius: 1rem;
    background: ${(props) => Color(props.accent).fade(0.7).string()};
  }
  *::-webkit-scrollbar-track {
    box-shadow: none;
  }
  *::selection {
    background: ${(props) => props.accent};
    color: ${(props) => props.background}
  }

  html {
    font-size: 62.5%;
  }
  
  body {
    scrollbar-face-color: ${(props) => props.accent};
    scrollbar-track-color: transparent;
  }
`;

export const ThemeProvider = ({ children }) => {
  const { Site } = useContext(UserContext);
  let theme;
  if (Site) {
    theme = Site.theme;
  } else {
    theme = "dark";
  }
  const colours = {
    background: theme === "dark" ? "#3D4562" : "#DDE5FF",
    backgroundDark: theme === "dark" ? "#2E3652" : "#C9D1EE",
    accent: theme === "dark" ? "#FFCFF3" : "#7D4D71",
    special: theme === "dark" ? "#97F4FF" : "#33909B",
    success: theme === "dark" ? "#C6FFBB" : "#629B57",
    error: theme === "dark" ? "#FF979F" : "#9B333B",
    text: theme === "dark" ? "#FFFFFF" : "#3D4562",
  };

  const materialTheme = createTheme({
    palette: {
      type: theme,
      background: {
        default: colours.background,
        paper: colours.backgroundDark,
      },
      primary: {
        main: colours.background,
        dark: colours.backgroundDark,
        contrastText: colours.text,
      },
      secondary: {
        main: colours.accent,
        contrastText: colours.background,
      },
      info: {
        main: colours.special,
      },
      text: {
        primary: colours.text,
        secondary: Color(colours.text).fade(0.45).string(),
        disabled: Color(colours.text).fade(0.75).string(),
      },
      success: {
        main: colours.success,
        dark: "#062200",
      },
      error: {
        main: colours.error,
      },
    },
    typography: {
      fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`,
      htmlFontSize: 10,
      button: {
        fontWeight: 800,
      },
      h4: {
        fontWeight: 700,
        fontSize: "2.4rem",
      },
      h5: {
        fontWeight: 800,
      },
    },
    overrides: {
      MuiButton: {
        sizeSmall: {
          padding: "2px 10px",
        },
      },
      MuiOutlinedInput: {
        root: {
          backgroundColor: colours.backgroundDark,
          borderRadius: 6,
        },
      },
      MuiTextField: {
        root: {
          "& input:valid + fieldset": {
            borderColor: colours.success,
          },
          "& input:invalid + fieldset": {
            borderColor: colours.error,
          },
          "& input:valid:focus + fieldset": {},
        },
      },
      MuiFormLabel: {
        root: {
          "&.Mui-focused": {
            color: "inherit",
          },
        },
      },
      MuiInputLabel: {
        shrink: { fontWeight: 700 },
        outlined: {
          fontStyle: "normal",
          fontWeight: 700,
        },
        root: {
          "&.MuiInputLabel-shrink": {
            fontWeight: 700,
            fontStyle: "italic",
          },
        },
        asterisk: {
          display: "none",
        },
      },
      MuiSelect: {
        root: {
          backgroundColor: colours.backgroundDark,
          "& input:valid + fieldset": {
            borderColor: colours.success,
          },
          "& input:invalid + fieldset": {
            borderColor: colours.error,
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          padding: "6px 10px",
          backgroundColor: colours.backgroundDark,
          fontSize: 18,
          color: colours.text,
        },
        arrow: {
          color: colours.backgroundDark,
        },
      },
      MuiSwitch: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: {
          padding: 1,
          "&$checked": {
            transform: "translateX(16px)",
            "& + $track": {
              opacity: 1,
              border: "none",
              backgroundColor: `${colours.success} !important`,
            },
            "& $thumb": {
              color: colours.text,
            },
          },
          "&$focusVisible $thumb": {
            border: `6px solid`,
          },
        },
        thumb: {
          width: 24,
          height: 24,
        },
        track: {
          borderRadius: 26 / 2,
          border: `1px solid ${colours.text}`,
          opacity: 1,
          backgroundColor: colours.backgroundDark,
        },
      },
      MuiDialogActions: {
        root: {
          padding: "1rem 2rem 2rem",
        },
      },
    },
    props: {
      MuiButton: {
        variant: "outlined",
        size: "small",
      },
      MuiTextField: {
        fullWidth: true,
        color: "secondary",
        variant: "outlined",
        margin: "normal",
        size: "small",
      },
      MuiSelect: {
        fullWidth: true,
        color: "secondary",
      },
      MuiFormControl: {
        fullWidth: true,
        variant: "outlined",
        margin: "normal",
        size: "small",
      },
      MuiPaper: {
        elevation: 0,
        variant: "outlined",
      },
      MuiTooltip: {
        placement: "top",
        arrow: true,
      },
      MuiAppBar: {
        color: "transparent",
      },
      MuiChip: {
        variant: "outlined",
      },
    },
  });

  const responsiveTheme = responsiveFontSizes(materialTheme);

  return (
    <MaterialThemeProvider theme={responsiveTheme}>
      <StyledThemeProvider theme={responsiveTheme}>
        <StylesProvider injectFirst>
          {/* <MuiPickersUtilsProvider utils={MomentUtils}> */}
          <CssBaseline />
          <GlobalStyle
            background={colours.background}
            accent={colours.accent}
          />
          {children}
          {/* </MuiPickersUtilsProvider> */}
        </StylesProvider>
      </StyledThemeProvider>
    </MaterialThemeProvider>
  );
};

export const SnackThemeProvider = ({ children }) => {
  const colours = {
    background: "#3D4562",
    backgroundDark: "#2E3652",
    accent: "#FFCFF3",
    special: "#97F4FF",
    success: "#C6FFBB",
    error: "#FF979F",
    text: "#FFFFFF",
  };

  const snackTheme = createTheme({
    palette: {
      type: "dark",
      background: {
        default: colours.background,
        paper: colours.backgroundDark,
      },
      primary: {
        main: colours.background,
        dark: colours.backgroundDark,
        contrastText: colours.text,
      },
      secondary: {
        main: colours.accent,
        contrastText: colours.background,
      },
      info: {
        main: colours.special,
      },
      text: {
        primary: colours.text,
        secondary: Color(colours.text).fade(0.45).string(),
        disabled: Color(colours.text).fade(0.75).string(),
      },
      success: {
        main: colours.success,
        dark: "#062200",
      },
      error: {
        main: colours.error,
      },
    },
  });
  return (
    <MaterialThemeProvider theme={snackTheme}>{children}</MaterialThemeProvider>
  );
};
